"use client";

import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { useParams } from "next/navigation";
import { cn } from "@/lib/utils";
import { RenderMarkdown } from "../render-markdown";

interface TwoColumnSectionProps {
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  isReversed?: boolean;
  buttonText?: string;
  buttonLink?: string;
}

export const TwoColumnSection = ({
  title,
  description,
  image,
  imageAlt,
  isReversed = false,
  buttonText,
  buttonLink,
}: TwoColumnSectionProps) => {
  const { locale } = useParams();
  return (
    <section className="my-14 lg:my-28 w-full h-full">
      <div className="w-full max-w-7xl px-4 mx-auto">
        <div
          className={cn(
            "flex flex-col gap-10 md:items-stretch",
            isReversed ? "md:flex-row-reverse" : "md:flex-row"
          )}
        >
          {/* Content Column */}
          <div className="flex flex-col gap-4 flex-1 self-center">
            <h2 className="text-4xl font-bold">{title}</h2>
            <div className="space-y-4">
              <RenderMarkdown>{description}</RenderMarkdown>
            </div>
            {buttonText && buttonLink && (
              <div>
                <Link
                  className="inline-flex items-center justify-center gap-2 bg-black text-white px-6 py-3 rounded-lg hover:bg-black/90 transition-all duration-300 hover:scale-105 ease-in-out font-medium text-sm"
                  href={buttonLink}
                  locale={locale as string}
                >
                  {buttonText}
                </Link>
              </div>
            )}
          </div>

          {/* Image Column */}
          <div className="flex-1 flex">
            <div className="relative w-full overflow-hidden rounded-lg min-h-[300px] lg:min-h-[400px] flex-1">
              <Image
                src={image}
                alt={imageAlt || "Image"}
                fill
                className="object-cover transition-transform duration-300 hover:scale-105 "
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 40vw"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
